import {AsyncStatus} from 'enums';
import debounce from 'lodash/debounce';

export type ISearcherOnSearchResponse<TResult> = Promise<{canLoadMore: boolean, results: TResult[]}>
export type ISearcherOnSearch<TResult> = (query: string, offset: number) => ISearcherOnSearchResponse<TResult>;

export interface ISearcherOptions {
    debounceDelay?: number,
}

// This class is designed to be used for search dropdowns or search pages.
// It abstracts the logic for debouncing searches, and for only showing the results
// from the most recent search.
export class Searcher<TResult> {
    public canLoadMore: boolean;
    public err: any;
    public results: TResult[];
    public status: AsyncStatus;

    private lastSearchQuery: string;
    private mostRecentSearchId = 0;

    constructor(
        public onSearch: ISearcherOnSearch<TResult>,
        private options: ISearcherOptions = {
            debounceDelay: 200,
        },
    ) {
        // Initialize default values. We're using reset to do this so that we only have 1 code path
        // for setting default values.
        this.reset();
    }

    public search = (query: string): Promise<TResult[]> => {
        this.setSearchProps(query);
        return this._search(query, false);
    }

    public searchDebounced = (query: string): Promise<TResult[]> => {
        // Even though we're debouncing the actual ajax call, we want to update our state immediately.
        this.setSearchProps(query);

        // Debounce doesn't support promises, so we need to create a promise,
        // then pass it to the debounced method so it can call resolve when it runs.
        return new Promise<TResult[]>((resolve, reject) => {
            this._searchDebounced(query, resolve, reject);
        });
    }

    private setSearchProps(query: string) {
        this.canLoadMore = false;
        this.err = undefined;
        this.lastSearchQuery = query;
        this.results = [];
        this.status = AsyncStatus.Loading;
        // Bump the most recent search id to make any earlier searches not fire their callbacks.
        this.mostRecentSearchId++;
    }

    // Loads more results if possible. Returns undefined if it cannot load more.
    public tryLoadMore = (): Promise<TResult[]> | undefined => {
        if (!this.canLoadMore) return undefined;

        this.canLoadMore = false;
        this.err = undefined;
        this.status = AsyncStatus.Loading;
        this.mostRecentSearchId++;

        return this._search(this.lastSearchQuery, true);
    }

    // This is a helper method to debounce the search promise.
    private _searchDebounced = debounce((query: string, resolve, reject) => {
        this._search(query, false)
            .then((results) => {
                resolve(results);
            })
            .catch((err) => {
                reject(err);
            });
    }, this.options.debounceDelay);

    // Internal search method that can either search, or load more results from the last search.
    private _search(query: string, isLoadMore: boolean): Promise<TResult[]> {
        const mySearchId = this.mostRecentSearchId;

        // Wrap the search promise so that we can choose not to resolve it if
        // the user searches again before we get a response.
        return new Promise<TResult[]>((resolve, reject) => {
            // Call the search method we were passed in order to get some results.
            this.onSearch(query, this.results.length)
                .then(({canLoadMore, results}) => {
                    // Don't do anything if the user has searched again.
                    if (mySearchId !== this.mostRecentSearchId) return;

                    this.canLoadMore = canLoadMore;
                    this.status = AsyncStatus.Success;

                    if (isLoadMore) {
                        this.results = this.results.concat(results);
                    } else {
                        this.results = results
                    }

                    resolve(this.results);
                }, (err) => {
                    // Don't do anything if the user has searched again.
                    if (mySearchId !== this.mostRecentSearchId) return;

                    console.error(err);
                    this.err = err;
                    this.status = AsyncStatus.Error;
                    reject(err);
                })
        });
    }

    public reset = () => {
        this.canLoadMore = false;
        this.err = undefined;
        this.results = [];
        this.status = AsyncStatus.Initial;
        this.lastSearchQuery = '';
        this.mostRecentSearchId++;
    }
}